import { I18n, createI18n } from 'vue-i18n';
import { Device } from '@capacitor/device';
import { Capacitor } from '@capacitor/core';
import createUseLocale, { UseLocale } from './use-locale';
import createUseLocalisation, { type UseLocalisation } from './use-localisation';
import { getLanguageState } from './store';
import { LanguageDefinition } from './types';
import { getRealmLanguage } from './realm-language';

export type I18nSetupOptions<L> = {
  appName: string;
  defaultLanguageName: string;
  defaultLanguage: L;
  supportedLanguages: LanguageDefinition[];
  /** Tried before the persisted and the realm language, e.g. a language the page was asked to show. */
  initialLanguages?: string[];
  additionalProjects?: { name: string; isoLanguage?: boolean; subProject?: boolean }[];
};

export type I18nSetup<L> = {
  i18n: I18n<any, any, any, string, false>;
  useLocalisation: UseLocalisation;
  useLocale: UseLocale<L>;
  changeLanguage: (language: string) => Promise<void>;
  /**
   *
   * @param projectName
   * @param {boolean} isoLanguage [isoLanguage=true]
   * @param {boolean} subProject [subProject=false] projects that requires main project to be loaded before merging the translation
   */
  includeAdditionalProject: (projectName: string, isoLanguage?: boolean, subProject?: boolean) => Promise<void>;
};

const setup = <L>({
  appName,
  defaultLanguageName,
  defaultLanguage,
  supportedLanguages,
  initialLanguages = [],
  additionalProjects = [],
}: I18nSetupOptions<L>): I18nSetup<L> => {
  const i18n = createI18n({
    legacy: false,
    locale: defaultLanguageName,
    fallbackLocale: defaultLanguageName,
    messages: { dev: defaultLanguage as any },
  });

  const useLocalisation = createUseLocalisation(
    appName,
    i18n,
    supportedLanguages,
    defaultLanguageName,
    additionalProjects,
  );
  const useLocale = createUseLocale<L>(defaultLanguage);

  const { isLanguageSupported, changeLanguage, includeAdditionalProject } = useLocalisation();

  (async () => {
    let languageIso;

    if (Capacitor.isNativePlatform()) {
      try {
        const { value } = await Device.getLanguageTag();
        const rawLanguage = value.toLowerCase().replace('_', '-').split('-')[0];

        languageIso = supportedLanguages.find((l) => l.short.toLowerCase() === rawLanguage)?.iso;
      } catch (e) {
        console.warn('Native language detection failed, falling back to realm', e);
      }
    }

    if (!languageIso) {
      languageIso = getRealmLanguage();
    }

    const language = [...initialLanguages, getLanguageState()?.selectedLanguage, languageIso]
      .filter((l) => l && isLanguageSupported(l))
      .find((l) => l);

    await changeLanguage(language ?? defaultLanguageName);
  })();

  return {
    i18n,
    useLocalisation,
    useLocale,
    changeLanguage,
    includeAdditionalProject,
  };
};

export default setup;
